/**
 * Expand a templated host into the literal hosts it stands for.
 *
 * The legacy model stored one `VideoRecorder` with a templated `device_ip` (`192.168.4.{}`) for a
 * whole subnet of IP cameras. The camera model forbids that: `CaptureDevice.host` carries the
 * CheckConstraint `capturedevice_host_no_curly_placeholder`, and one IP camera is one device. The
 * placeholder therefore survives only as an *input* convenience — expanded here, client-side,
 * before anything is sent.
 *
 * The braced grammar is the scan's slot-range grammar (`screenshots.py::parse_slot_ranges`, mirrored
 * in `DeviceDiscoveryPanel::parseSlotRanges`): values and inclusive `a-b` ranges, comma-separated.
 * One syntax for the operator to learn, in both places numbers are typed.
 */

const PLACEHOLDER = "{}";
const OPEN = "{";
const CLOSE = "}";

/**
 * Cap on one expansion — same order as the scan's `MAX_SCAN_SLOTS`.
 *
 * Reaching it is an error, never a truncation: a silently shortened range would create a partial
 * fleet, and the operator would have no way to see which half is missing.
 */
export const MAX_HOSTS = 256;

/**
 * Whether a value is templated — any brace, not just a bare `{}`.
 *
 * Used both on the host (`{10-40}` carries its range inside the braces) and on the suffix, whose
 * only legal placeholder is `{}`: either brace means "templated" and is enough to decide the
 * host/suffix mutual exclusion.
 *
 * @param {string|null|undefined} value - The value to inspect.
 * @returns {boolean} True when a brace is present.
 */
export function hasPlaceholder(value) {
  const text = String(value ?? "");
  return text.includes(OPEN) || text.includes(CLOSE);
}

/**
 * Substitute every `{}` in a value with the number that produced a host.
 *
 * @param {string} value - The template, possibly without any placeholder.
 * @param {number|null} token - The number to inject, or null for a literal (untemplated) host.
 * @returns {string} The expanded value, unchanged when there is nothing to substitute.
 */
export function expandToken(value, token) {
  const text = String(value ?? "");
  if (token == null) return text;
  return text.split(PLACEHOLDER).join(String(token));
}

/** The single `{…}` group, or the reason there is not exactly one. */
function splitTemplate(text) {
  const open = text.indexOf(OPEN);
  const close = text.indexOf(CLOSE);
  const singleGroup =
    open !== -1 && close > open && text.indexOf(OPEN, open + 1) === -1 && text.indexOf(CLOSE, close + 1) === -1;
  if (open === -1 && close === -1) return { group: null, error: null };
  if (!singleGroup) {
    return { group: null, error: "A templated host takes exactly one {…} group, e.g. 192.168.4.{10-40}." };
  }
  return {
    group: { prefix: text.slice(0, open), body: text.slice(open + 1, close), suffix: text.slice(close + 1) },
    error: null,
  };
}

/** Expand the braced body into ascending, de-duplicated numbers. */
function parseNumbers(body) {
  const text = body.trim();
  if (!text) return { numbers: [], error: "The {…} placeholder is empty — give it a range, e.g. {10-40}." };

  const numbers = new Set();
  const unreadable = (part) => ({
    numbers: [],
    error: `Could not read "${part}" — use e.g. {10-40} or {10,12,20-24}.`,
  });

  const parts = text.split(",");
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index].trim();
    if (!part) continue;
    if (part.includes("-")) {
      if ((part.match(/-/g) || []).length > 1) return unreadable(part);
      const [rawStart, rawEnd] = part.split("-", 2);
      if (!/^\d+$/.test(rawStart.trim()) || !/^\d+$/.test(rawEnd.trim())) return unreadable(part);
      const start = Number(rawStart);
      const end = Number(rawEnd);
      if (end < start) return { numbers: [], error: `"${part}" ends before it starts.` };
      // Bound the span before expanding it, so a 1-100000 typo never allocates it.
      if (end - start + 1 > MAX_HOSTS) {
        return { numbers: [], error: `That range covers more than ${MAX_HOSTS} hosts (max ${MAX_HOSTS}).` };
      }
      for (let value = start; value <= end; value += 1) numbers.add(value);
    } else {
      if (!/^\d+$/.test(part)) return unreadable(part);
      numbers.add(Number(part));
    }
    if (numbers.size > MAX_HOSTS) {
      return { numbers: [], error: `That range covers more than ${MAX_HOSTS} hosts (max ${MAX_HOSTS}).` };
    }
  }

  if (numbers.size === 0)
    return { numbers: [], error: "The {…} placeholder is empty — give it a range, e.g. {10-40}." };
  return { numbers: [...numbers].sort((a, b) => a - b), error: null };
}

/**
 * Expand a host input into the literal hosts to create.
 *
 * @param {string} input - A literal host, or one carrying a single `{…}` range.
 * @returns {{hosts: Array<{host: string, token: number|null}>, error: string|null}} The hosts in
 *   ascending order — `token` is the number that produced each one, null for a literal host — or
 *   the reason nothing could be expanded.
 */
export default function expandHostRange(input) {
  const text = String(input ?? "").trim();
  if (!text) return { hosts: [], error: "Host is mandatory (e.g. 192.168.4.10 or 192.168.4.{10-40})." };

  const { group, error: groupError } = splitTemplate(text);
  if (groupError) return { hosts: [], error: groupError };
  if (!group) return { hosts: [{ host: text, token: null }], error: null };

  const { numbers, error } = parseNumbers(group.body);
  if (error) return { hosts: [], error };
  return {
    hosts: numbers.map((token) => ({ host: `${group.prefix}${token}${group.suffix}`, token })),
    error: null,
  };
}
